/**
 * Formats numbers into tier-based compact strings with a '+' suffix (e.g. 1+, 2+, 5+, 40+, 90+, 100+, 1.8k+, 1.2M+).
 * Never exposes the exact raw number for privacy / commercial presentation.
 *
 * @param count Raw number
 * @returns Formatted compact tier string with '+'
 */
export function formatCompactNumber(count?: number): string {
  const n = count || 0;
  if (n <= 0) return '0';

  if (n >= 1_000_000) {
    const millions = Math.floor(n / 100_000) / 10;
    return `${millions.toFixed(1).replace(/\.0$/, '')}M+`;
  }
  if (n >= 1_000) {
    const thousands = Math.floor(n / 100) / 10;
    return `${thousands.toFixed(1).replace(/\.0$/, '')}k+`;
  }
  if (n >= 100) {
    const hundreds = Math.floor(n / 100) * 100;
    return `${hundreds}+`;
  }
  if (n >= 10) {
    const tens = Math.floor(n / 10) * 10;
    return `${tens}+`;
  }
  if (n >= 5) {
    return '5+';
  }
  if (n >= 2) {
    return '2+';
  }
  return '1+';
}


/**
 * Formats sold quantity string with language support ('1.8k+ đã bán' / '1.8k+ sold').
 *
 * @param count Sold quantity
 * @param currentLang Current active language code ('vi' | 'en')
 * @returns Formatted localized sold string
 */
export function formatSoldQuantity(count?: number, currentLang: string = 'vi'): string {
  const formatted = formatCompactNumber(count);
  const isVi = currentLang === 'vi' || !currentLang;
  return isVi ? `${formatted} đã bán` : `${formatted} sold`;
}

/**
 * Formats like / wishlist count with language support.
 *
 * @param count Like quantity
 * @param currentLang Current active language code ('vi' | 'en')
 * @returns Formatted localized like string
 */
export function formatLikeQuantity(count?: number, currentLang: string = 'vi'): string {
  const formatted = formatCompactNumber(count);
  const isVi = currentLang === 'vi' || !currentLang;
  return isVi ? `${formatted} lượt thích` : `${formatted} likes`;
}
